import "./App.css";
import { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import ScrollToTop from "./components/shared/ScrollToTop";

// ── EAGER: critical first-paint + globally-mounted components ──
// HomePage is the LCP-critical landing route, so we keep it in the main
// bundle to avoid a Suspense flash on the highest-traffic surface.
// NotFoundPage stays eager so it still works if a chunk fetch fails.
import HomePage from "./pages/HomePage";
import NotFoundPage from "./pages/NotFoundPage";
import ExitIntentModal from "./components/shared/ExitIntentModal";
import WhatsAppBubble from "./components/shared/WhatsAppBubble";
import StickyExitCTA from "./components/shared/StickyExitCTA";
import LeadDraftCapture from "./components/shared/LeadDraftCapture";

// ── LAZY: every other route gets its own chunk ──
// React.lazy() splits the bundle on the dynamic `import()`. CRA/CRACO
// handles the webpack chunking automatically.
const AsoPage = lazy(() => import("./pages/AsoPage"));
const AppDevelopmentPage = lazy(() => import("./pages/AppDevelopmentPage"));
const UserAcquisitionPage = lazy(() => import("./pages/UserAcquisitionPage"));
const AppRetentionPage = lazy(() => import("./pages/AppRetentionPage"));
const RatingsReviewsPage = lazy(() => import("./pages/RatingsReviewsPage"));
const GeoAiCitationPage = lazy(() => import("./pages/GeoAiCitationPage"));
const AnalyticsPage = lazy(() => import("./pages/AnalyticsPage"));
const InfluencerMarketingPage = lazy(
  () => import("./pages/InfluencerMarketingPage"),
);
const ContentMarketingPage = lazy(() => import("./pages/ContentMarketingPage"));
const AdvancedPaidMarketingPage = lazy(
  () => import("./pages/AdvancedPaidMarketingPage"),
);
const GTMStrategyPage = lazy(() => import("./pages/GTMStrategyPage"));
const CreativeProductionPage = lazy(
  () => import("./pages/CreativeProductionPage"),
);
const InternationalExpansionPage = lazy(
  () => import("./pages/InternationalExpansionPage"),
);
const ConsultancyPage = lazy(() => import("./pages/ConsultancyPage"));
const CaseStudiesPage = lazy(() => import("./pages/CaseStudiesPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PartnersPage = lazy(() => import("./pages/PartnersPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const CareerApplyPage = lazy(() => import("./pages/CareerApplyPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const AppLaunchChecklistPage = lazy(
  () => import("./pages/AppLaunchChecklistPage"),
);
const KeywordResearchTemplatePage = lazy(
  () => import("./pages/KeywordResearchTemplatePage"),
);
const AppLTVCalculatorPage = lazy(() => import("./pages/AppLTVCalculatorPage"));
const BuildYourPlanPage = lazy(() => import("./pages/BuildYourPlanPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const MarketHomePage = lazy(() => import("./pages/MarketHomePage"));
const FreeAsoAuditPage = lazy(() => import("./pages/FreeAsoAuditPage"));
const AppVsAppPage = lazy(() => import("./pages/AppVsAppPage"));
const AsoReportPage = lazy(() => import("./pages/AsoReportPage"));
const AsoReportsHubPage = lazy(() => import("./pages/AsoReportsHubPage"));
const PlaybookPage = lazy(() => import("./pages/PlaybookPage"));
const AsoReportsGenrePage = lazy(() => import("./pages/AsoReportsGenrePage"));
const AsoEnginePage = lazy(() => import("./pages/AsoEnginePage"));
const KeywordRankPage = lazy(() => import("./pages/KeywordRankPage"));
const ReviewSentimentPage = lazy(() => import("./pages/ReviewSentimentPage"));
const AsoMonitorPage = lazy(() => import("./pages/AsoMonitorPage"));
const BlogDetailPage = lazy(() => import("./pages/BlogDetailPage"));
const CaseStudyDetailPage = lazy(() => import("./pages/CaseStudyDetailPage"));
const AppCategoryPage = lazy(() => import("./pages/AppCategoryPage"));
const AllCategoriesPage = lazy(() => import("./pages/AllCategoriesPage"));
const AllMarketsPage = lazy(() => import("./pages/AllMarketsPage"));
const AuditReportPage = lazy(() => import("./pages/AuditReportPage"));
const AuditPackagePage = lazy(() => import("./pages/AuditPackagePage"));
const AuditSharePage = lazy(() => import("./pages/AuditSharePage"));
const ServiceMarketPage = lazy(() => import("./pages/ServiceMarketPage"));
const AsoScoreCheckerPage = lazy(() => import("./pages/AsoScoreCheckerPage"));
const VerticalPage = lazy(() => import("./pages/VerticalPage"));
const ComparisonPage = lazy(() => import("./pages/ComparisonPage"));
const ComparisonsHubPage = lazy(() => import("./pages/ComparisonsHubPage"));
const MediaKitPage = lazy(() => import("./pages/MediaKitPage"));
const AppRevenueEstimatorPage = lazy(
  () => import("./pages/AppRevenueEstimatorPage"),
);
const GlossaryPage = lazy(() => import("./pages/GlossaryPage"));
const GlossaryEntryPage = lazy(() => import("./pages/GlossaryEntryPage"));
const BestOfPage = lazy(() => import("./pages/BestOfPage"));
const BenchmarkReportPage = lazy(() => import("./pages/BenchmarkReportPage"));
const AppMarketingLandingPage = lazy(
  () => import("./pages/AppMarketingLandingPage"),
);
const StatusPage = lazy(() => import("./pages/StatusPage"));
const PrivacySecurityPage = lazy(() => import("./pages/PrivacySecurityPage"));
const PressPage = lazy(() => import("./pages/PressPage"));
const ReferralPage = lazy(() => import("./pages/ReferralPage"));
const WhatsWorkingPage = lazy(() => import("./pages/WhatsWorkingPage"));
const OfficeHoursPage = lazy(() => import("./pages/OfficeHoursPage"));
const TalkWithUsPage = lazy(() => import("./pages/TalkWithUsPage"));
const OpenSourcePage = lazy(() => import("./pages/OpenSourcePage"));
const StoreListingPreviewPage = lazy(
  () => import("./pages/StoreListingPreviewPage"),
);
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const CookiesPage = lazy(() => import("./pages/CookiesPage"));
const DiscoveryQuestionnairePage = lazy(
  () => import("./pages/DiscoveryQuestionnairePage"),
);
const RoiCalculatorPage = lazy(() => import("./pages/RoiCalculatorPage"));

/* ─── Suspense fallback ──────────────────────────────────────
 * Minimal top-of-page loading bar — no flash of blank screen,
 * no layout shift. Auto-hides when the chunk resolves.
 * ──────────────────────────────────────────────────────────── */
// Pre-rendered pages (scripts/prerender.js) arrive with their HTML already in
// #root. Read before React mounts so the first fallback keeps showing it while
// the route chunk loads, instead of blanking the page. Later navigations get
// the loading bar as before.
let prerenderedHtml = document.getElementById("root")?.innerHTML || "";

function RouteFallback() {
  useEffect(() => () => { prerenderedHtml = ""; }, []);
  if (prerenderedHtml) {
    return <div dangerouslySetInnerHTML={{ __html: prerenderedHtml }} />;
  }
  return (
    <div
      role="status"
      aria-label="Loading page"
      className="fixed top-0 left-0 right-0 z-[100] pointer-events-none"
    >
      <div className="h-0.5 bg-electric/20 overflow-hidden">
        <div className="h-full w-1/3 bg-electric animate-[routeloader_1.2s_ease-in-out_infinite]" />
      </div>
      <style>{`
        @keyframes routeloader {
          0%   { transform: translateX(-100%); }
          50%  { transform: translateX(150%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <LeadDraftCapture />
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<HomePage />} />

          {/* Programmatic SEO matrix — 8 services × 12 markets = 96 pages
              URL pattern: /:service/in/:market   (e.g. /aso/in/japan, /user-acquisition/in/uk) */}
          <Route path="/:service/in/:market" element={<ServiceMarketPage />} />

          <Route path="/aso" element={<AsoPage />} />
          {/* Canonical app-marketing-by-category route. Old /aso/:slug URLs redirect via AppCategoryPage. */}
          <Route
            path="/app-marketing/:categorySlug"
            element={<AppCategoryPage />}
          />
          <Route path="/aso/:categorySlug" element={<AppCategoryPage />} />
          <Route path="/app-categories" element={<AllCategoriesPage />} />
          <Route path="/markets" element={<AllMarketsPage />} />
          <Route path="/app-development" element={<AppDevelopmentPage />} />
          <Route path="/user-acquisition" element={<UserAcquisitionPage />} />
          <Route path="/app-retention" element={<AppRetentionPage />} />
          <Route path="/ratings-reviews" element={<RatingsReviewsPage />} />
          <Route path="/geo-ai-citation" element={<GeoAiCitationPage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route
            path="/influencer-marketing"
            element={<InfluencerMarketingPage />}
          />
          <Route path="/content-marketing" element={<ContentMarketingPage />} />
          <Route
            path="/advanced-paid-marketing"
            element={<AdvancedPaidMarketingPage />}
          />
          <Route path="/gtm-strategy" element={<GTMStrategyPage />} />
          <Route
            path="/creative-production"
            element={<CreativeProductionPage />}
          />
          <Route
            path="/international-expansion"
            element={<InternationalExpansionPage />}
          />
          <Route path="/consultancy" element={<ConsultancyPage />} />
          <Route path="/case-studies" element={<CaseStudiesPage />} />
          <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogDetailPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/about-us" element={<AboutPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/careers/apply" element={<CareerApplyPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/resources" element={<ResourcesPage />} />
          <Route
            path="/resources/app-launch-checklist"
            element={<AppLaunchChecklistPage />}
          />
          <Route
            path="/resources/keyword-research-template"
            element={<KeywordResearchTemplatePage />}
          />
          <Route
            path="/resources/app-ltv-calculator"
            element={<AppLTVCalculatorPage />}
          />
          <Route
            path="/resources/aso-score-checker"
            element={<AsoScoreCheckerPage />}
          />
          <Route
            path="/resources/app-revenue-estimator"
            element={<AppRevenueEstimatorPage />}
          />
          <Route
            path="/resources/roi-calculator"
            element={<RoiCalculatorPage />}
          />
          <Route
            path="/resources/benchmark-report-2026"
            element={<BenchmarkReportPage />}
          />

          {/* Vertical landing pages */}
          <Route
            path="/gaming-app-marketing"
            element={<VerticalPage slugOverride="gaming" />}
          />
          <Route
            path="/fintech-app-marketing"
            element={<VerticalPage slugOverride="fintech" />}
          />
          <Route
            path="/edtech-app-marketing"
            element={<VerticalPage slugOverride="edtech" />}
          />
          <Route
            path="/health-app-marketing"
            element={<VerticalPage slugOverride="health" />}
          />
          <Route
            path="/ecommerce-app-marketing"
            element={<VerticalPage slugOverride="ecommerce" />}
          />
          <Route
            path="/web-app-marketing"
            element={<VerticalPage slugOverride="webapp" />}
          />

          {/* Comparisons hub */}
          <Route path="/compare" element={<ComparisonsHubPage />} />
          <Route path="/media-kit" element={<MediaKitPage />} />

          {/* Comparison pages — one route per competitor (avoids React Router hyphen-param limitation) */}
          <Route
            path="/appfillip-vs-phiture"
            element={<ComparisonPage slugOverride="phiture" />}
          />
          <Route
            path="/appfillip-vs-yodel"
            element={<ComparisonPage slugOverride="yodel" />}
          />
          <Route
            path="/appfillip-vs-gummicube"
            element={<ComparisonPage slugOverride="gummicube" />}
          />
          <Route
            path="/appfillip-vs-storemaven"
            element={<ComparisonPage slugOverride="storemaven" />}
          />

          {/* Glossary */}
          <Route path="/glossary" element={<GlossaryPage />} />
          <Route path="/glossary/:slug" element={<GlossaryEntryPage />} />

          {/* Best-of lists */}
          <Route
            path="/best-aso-tools-2026"
            element={<BestOfPage slugOverride="best-aso-tools-2026" />}
          />
          <Route
            path="/best-mmps-2026"
            element={<BestOfPage slugOverride="best-mmps-2026" />}
          />

          {/* Trust + community surfaces */}
          <Route path="/status" element={<StatusPage />} />
          <Route path="/privacy-security" element={<PrivacySecurityPage />} />
          <Route path="/press" element={<PressPage />} />
          <Route path="/referral" element={<ReferralPage />} />
          <Route path="/whats-working" element={<WhatsWorkingPage />} />
          <Route path="/office-hours" element={<OfficeHoursPage />} />
          <Route path="/talk-with-us" element={<TalkWithUsPage />} />
          <Route path="/open-source" element={<OpenSourcePage />} />
          <Route
            path="/resources/store-listing-preview"
            element={<StoreListingPreviewPage />}
          />

          {/* Legal pages — linked from footer */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/discovery" element={<DiscoveryQuestionnairePage />} />

          <Route path="/pricing" element={<PricingPage />} />
          <Route path="/build-your-plan" element={<BuildYourPlanPage />} />
          <Route path="/free-aso-audit" element={<FreeAsoAuditPage />} />
          <Route path="/compare-apps" element={<AppVsAppPage />} />
          {/* Programmatic SEO: public per-app ASO reports + hub */}
          <Route path="/playbook" element={<PlaybookPage />} />
          <Route path="/aso-reports" element={<AsoReportsHubPage />} />
          <Route path="/aso-reports/:genre" element={<AsoReportsGenrePage />} />
          <Route path="/aso-report/:slug" element={<AsoReportPage />} />
          <Route path="/tools/aso-engine" element={<AsoEnginePage />} />
          <Route path="/keyword-rank-checker" element={<KeywordRankPage />} />
          <Route path="/review-analyzer" element={<ReviewSentimentPage />} />
          <Route path="/aso-monitor" element={<AsoMonitorPage />} />
          {/* Lean, conversion-tuned landing pages for paid ads (no mega-nav) */}
          <Route path="/lp/audit" element={<FreeAsoAuditPage lean />} />
          <Route path="/lp/compare" element={<AppVsAppPage lean />} />
          <Route path="/lp/keyword" element={<KeywordRankPage lean />} />
          <Route path="/lp/reviews" element={<ReviewSentimentPage lean />} />
          <Route path="/lp/monitor" element={<AsoMonitorPage lean />} />
          <Route path="/audit-report" element={<AuditReportPage />} />
          <Route path="/audit-package" element={<AuditPackagePage />} />
          <Route path="/audit/share/:auditId" element={<AuditSharePage />} />

          {/* Google Ads landing page — no navbar/footer */}
          <Route
            path="/lp/app-marketing"
            element={<AppMarketingLandingPage />}
          />

          {/* Localized country homepages — MUST remain near last; MarketHomePage renders NotFound for invalid slugs */}
          <Route path="/:market" element={<MarketHomePage />} />

          {/* Catch-all 404 — multi-segment unknown URLs (eager, so it works even if chunks fail) */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
      <ExitIntentModal />
      <StickyExitCTA />
      <WhatsAppBubble />
      <Toaster position="bottom-right" richColors closeButton />
    </BrowserRouter>
  );
}

export default App;
